import Immutable from "immutable";
import DesignUpdate from "../vo/DesignUpdate";
import AppConstants from "../constants/AppConstants";
import { RECENT_PEXELS_PHOTOS_KEY } from "../constants/DesignConstants.mjs";

export const MAX_RECENT_PEXELS_PHOTOS = 30;

/**
 * The subset worth persisting: enough to rebuild element content offline.
 * @param {import("@/types/data/Pexels.mjs").PexelsPhoto} photo
 * @returns {Object}
 */
export const toRecentPexelsPhoto = (photo) => ({
  id: photo.id,
  width: photo.width,
  height: photo.height,
  url: photo.url,
  alt: photo.alt || "",
  avgColor: photo.avgColor || "",
  photographer: photo.photographer || "",
  photographerUrl: photo.photographerUrl || "",
  original: photo.original,
  thumbnail: photo.thumbnail,
});

/**
 * Promotes a photo to the front, de-duplicating and enforcing the MRU cap.
 * @param {Immutable.List|Array|undefined} recents
 * @param {import("@/types/data/Pexels.mjs").PexelsPhoto} photo
 * @returns {Immutable.List}
 */
export const addRecentPexelsPhoto = (recents, photo) => {
  const list = Immutable.List.isList(recents) ? recents : Immutable.fromJS(recents || []);

  if (!photo || photo.id === undefined || photo.id === null) {
    return list.take(MAX_RECENT_PEXELS_PHOTOS);
  }

  const entry = Immutable.Map(toRecentPexelsPhoto(photo));

  return list
    .filterNot((item) => Immutable.Map.isMap(item) && item.get("id") === photo.id)
    .unshift(entry)
    .take(MAX_RECENT_PEXELS_PHOTOS);
};

/**
 * @param {Immutable.List|Array|undefined} recents
 * @returns {Object[]}
 */
export const getRecentPexelsPhotos = (recents) => {
  if (!recents) return [];

  const list = Immutable.List.isList(recents) ? recents : Immutable.fromJS(recents);
  if (!Immutable.List.isList(list)) return [];

  return list
    .map((item) => (Immutable.Map.isMap(item) ? item.toJS() : item))
    .filter((item) => item && item.id !== undefined && item.original)
    .take(MAX_RECENT_PEXELS_PHOTOS)
    .toJS();
};

/**
 * Uses SET because DesignUpdate's array merge cannot reorder or truncate the MRU list.
 * @param {Immutable.List|Array|undefined} recents
 * @param {import("@/types/data/Pexels.mjs").PexelsPhoto} photo
 * @returns {DesignUpdate}
 */
export const buildRecentPexelsPhotosUpdate = (recents, photo) => {
  const update = new DesignUpdate();

  update.add(
    RECENT_PEXELS_PHOTOS_KEY,
    addRecentPexelsPhoto(recents, photo),
    AppConstants.UPDATE_ACTION_SET,
  );

  return update;
};

const PexelsRecentsUtils = {
  buildRecentPexelsPhotosUpdate,
  addRecentPexelsPhoto,
  getRecentPexelsPhotos,
  toRecentPexelsPhoto,
  MAX_RECENT_PEXELS_PHOTOS,
};

export default PexelsRecentsUtils;
